import { atom } from 'jotai';
import { Folder, CreateFolderDto, UpdateFolderDto } from '../../types/folder';
import { translationEnabledAtom } from '../v0/translationAtoms';
import i18n from '../../lib/i18n';
import { FolderService } from '../../services/folderService';

/**
 * FolderV3 页面状态管理
 */

// 文件夹列表原子
export const foldersAtom = atom<Folder[]>([]);

// 文件夹加载状态原子
export const foldersLoadingAtom = atom<boolean>(false);

// 文件夹错误状态原子
export const foldersErrorAtom = atom<string | null>(null);

// 后台刷新状态原子
export const foldersBackgroundRefreshingAtom = atom<boolean>(false);

// 获取文件夹列表的原子
export const fetchFoldersAtom = atom(
  null,
  async (get, set) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return;
    }
    // --- End Translation Check ---

    set(foldersLoadingAtom, true);
    set(foldersErrorAtom, null);

    try {
      const data = await FolderService.getFolders();
      set(foldersAtom, data);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
      set(foldersErrorAtom, err instanceof Error ? err.message : '未知错误');
    } finally {
      set(foldersLoadingAtom, false);
    }
  }
);

// 后台刷新文件夹列表的原子
export const backgroundRefreshFoldersAtom = atom(
  null,
  async (get, set) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      return;
    }
    // --- End Translation Check ---

    set(foldersBackgroundRefreshingAtom, true);

    try {
      const data = await FolderService.getFolders();
      set(foldersAtom, data);
    } catch (err) {
      console.error('Failed to background refresh folders:', err);
      // 后台刷新失败时不设置错误状态，避免干扰用户
    } finally {
      set(foldersBackgroundRefreshingAtom, false);
    }
  }
);

// 创建文件夹的原子
export const createFolderAtom = atom(
  null,
  async (get, set, folderData: CreateFolderDto) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(foldersLoadingAtom, true);
    try {
      const newFolder = await FolderService.createFolder(folderData);

      // 重新获取文件夹列表
      const data = await FolderService.getFolders();
      set(foldersAtom, data);

      return newFolder;
    } catch (err) {
      console.error('Failed to create folder:', err);
      set(foldersErrorAtom, err instanceof Error ? err.message : '未知错误');
      return null;
    } finally {
      set(foldersLoadingAtom, false);
    }
  }
);

// 更新文件夹的原子
export const updateFolderAtom = atom(
  null,
  async (get, set, updateData: UpdateFolderDto) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(foldersLoadingAtom, true);
    try {
      const updatedFolder = await FolderService.updateFolder(updateData);

      // 重新获取文件夹列表
      const data = await FolderService.getFolders();
      set(foldersAtom, data);

      return updatedFolder;
    } catch (err) {
      console.error('Failed to update folder:', err);
      set(foldersErrorAtom, err instanceof Error ? err.message : '未知错误');
      return null;
    } finally {
      set(foldersLoadingAtom, false);
    }
  }
);

// 删除文件夹的原子
export const deleteFolderAtom = atom(
  null,
  async (get, set, folderId: string) => {
    // --- Translation Check ---
    if (get(translationEnabledAtom)) {
      alert(i18n.t('v0/common:errors.editingDisabledWhileTranslating'));
      return null;
    }
    // --- End Translation Check ---

    set(foldersLoadingAtom, true);
    try {
      const deletedFolder = await FolderService.deleteFolder(folderId);

      // 重新获取文件夹列表
      const data = await FolderService.getFolders();
      set(foldersAtom, data);

      return deletedFolder;
    } catch (err) {
      console.error('Failed to delete folder:', err);
      set(foldersErrorAtom, err instanceof Error ? err.message : '未知错误');
      return null;
    } finally {
      set(foldersLoadingAtom, false);
    }
  }
);
